import { useState } from "react";
import { ASSET_PATHS } from "./assets";
import { all_links } from "./links";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ContactSection = () => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validateForm = () => {
    let valid = true;
    let newErrors = {};

    if (!form.first_name.trim()) {
      newErrors.first_name = "First name is required.";
      valid = false;
    }
    if (!form.last_name.trim()) {
      newErrors.last_name = "Last name is required.";
      valid = false;
    }
    if (!form.email.trim()) {
      newErrors.email = "Email is required.";
      valid = false;
    } else if (!EMAIL_REGEX.test(form.email)) {
      newErrors.email = "Invalid email format.";
      valid = false;
    }
    if (!form.phone.trim()) {
      newErrors.phone = "Phone is required.";
      valid = false;
    }
    if (!form.message.trim()) {
      newErrors.message = "Message is required.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    try {
      await new Promise((res) => setTimeout(res, 1000));
      setForm({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // MODIFICATION: Removed min-h-screen, adjusted padding, and added relative for the background glow
    <section
      id="contact"
      className="relative bg-slate-950 text-slate-200 py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 font-kodeMono overflow-hidden"
    >
      {/* NEW: Decorative background glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80rem] h-[50rem] bg-[radial-gradient(circle_at_center,_rgba(34,211,238,0.1),_transparent_40%)] opacity-70 blur-3xl -z-0"
        aria-hidden="true"
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <header className="text-center mb-12 lg:mb-16">
          <h1 className="text-5xl font-extrabold text-white sm:text-6xl drop-shadow-lg">
            Get in <span className="text-cyan-400 glow-text-cyan">Touch</span>
          </h1>
        </header>

        <div className="lg:flex lg:gap-12 xl:gap-16">
          {/* Contact Info + Social Links */}
          <div className="lg:w-2/5 xl:w-1/3 space-y-10 mb-12 lg:mb-0">
            {/* ... (Your Direct Support card code is perfect, no changes needed) */}
            <div className="bg-slate-700/40 backdrop-blur-xl border border-slate-700/50 shadow-2xl rounded-xl p-6">
              <h2 className="text-3xl font-bold mb-6 text-white flex items-center">
                <img
                  src="/assets/icons/mail.svg"
                  alt="Mail"
                  className="w-9 h-9 mr-3 text-cyan-400"
                />
                Direct Support
              </h2>
              <p className="mb-4 text-slate-400">
                Email us directly for quick help:
              </p>
              <a
                href={all_links.support_mail}
                className="inline-flex items-center text-xl text-cyan-400 hover:text-white transition-all duration-200 group border-dashed border border-cyan-400/30 hover:border-cyan-400/80 hover:bg-cyan-400/10 backdrop-blur-sm rounded-lg px-4 py-3 w-full justify-center"
              >
                support@captchasonic.com
                <span className="ml-2 transition-transform group-hover:translate-x-1.5">
                  →
                </span>
              </a>
            </div>

            {/* ... (Your Join Our Community card code is perfect, no changes needed) */}
            <div className="bg-slate-700/40 backdrop-blur-xl border border-slate-700/50 shadow-2xl rounded-xl p-6">
              <h3 className="text-3xl font-bold mb-6 text-white">
                Join Our Community
              </h3>
              <p className="mb-6 text-slate-400">
                Connect with us on your favorite platforms:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                {[
                  {
                    href: all_links.github,
                    icon: ASSET_PATHS.contact_icons.github,
                    label: "GitHub",
                  },
                  {
                    href: all_links.telegram,
                    icon: ASSET_PATHS.contact_icons.telegram,
                    label: "Telegram",
                  },
                  {
                    href: all_links.discord,
                    icon: ASSET_PATHS.contact_icons.discord,
                    label: "Discord",
                  },
                ].map(({ href, icon, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col items-center justify-center p-4 border border-cyan-400/30 hover:border-cyan-400/80 hover:bg-cyan-400/10 backdrop-blur-sm rounded-lg transition-all duration-200 transform hover:scale-105"
                  >
                    <img
                      src={icon}
                      alt={label}
                      className="w-12 h-12 mb-2 group-hover:scale-110 transition-transform duration-200"
                    />
                    <span className="font-medium text-slate-300 group-hover:text-cyan-400">
                      {label}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:w-3/5 xl:w-2/3 bg-slate-700/40 backdrop-blur-xl border border-slate-700/50 shadow-2xl rounded-xl p-8">
            {/* ... (Your form code is perfect, no changes needed) */}
            <h2 className="text-3xl font-bold mb-8 text-white">
              Send Us a Message
            </h2>
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <div>
                  <input
                    type="text"
                    name="first_name"
                    placeholder="First Name *"
                    value={form.first_name}
                    onChange={handleChange}
                    className={`bg-transparent border-b-2 p-3 w-full text-white placeholder-slate-400/70 focus:outline-none transition-colors duration-200 ${
                      errors.first_name
                        ? "border-red-500/70 focus:border-red-400/80"
                        : "border-slate-600/70 focus:border-cyan-400"
                    }`}
                    required
                  />
                  {errors.first_name && (
                    <p className="text-red-400 text-xs mt-1.5">
                      {errors.first_name}
                    </p>
                  )}
                </div>
                <div>
                  <input
                    type="text"
                    name="last_name"
                    placeholder="Last Name *"
                    value={form.last_name}
                    onChange={handleChange}
                    className={`bg-transparent border-b-2 p-3 w-full text-white placeholder-slate-400/70 focus:outline-none transition-colors duration-200 ${
                      errors.last_name
                        ? "border-red-500/70 focus:border-red-400/80"
                        : "border-slate-600/70 focus:border-cyan-400"
                    }`}
                    required
                  />
                  {errors.last_name && (
                    <p className="text-red-400 text-xs mt-1.5">
                      {errors.last_name}
                    </p>
                  )}
                </div>
              </div>
              <input
                type="email"
                name="email"
                placeholder="Email Address *"
                value={form.email}
                onChange={handleChange}
                className={`bg-transparent border-b-2 p-3 w-full text-white placeholder-slate-400/70 focus:outline-none transition-colors duration-200 ${
                  errors.email
                    ? "border-red-500/70 focus:border-red-400/80"
                    : "border-slate-600/70 focus:border-cyan-400"
                }`}
                required
              />
              {errors.email && (
                <p className="text-red-400 text-xs mt-1.5">{errors.email}</p>
              )}

              <input
                type="tel"
                name="phone"
                placeholder="Phone Number *"
                value={form.phone}
                onChange={handleChange}
                className={`bg-transparent border-b-2 p-3 w-full text-white placeholder-slate-400/70 focus:outline-none transition-colors duration-200 ${
                  errors.phone
                    ? "border-red-500/70 focus:border-red-400/80"
                    : "border-slate-600/70 focus:border-cyan-400"
                }`}
                required
              />
              {errors.phone && (
                <p className="text-red-400 text-xs mt-1.5">{errors.phone}</p>
              )}

              <textarea
                name="message"
                rows="5"
                placeholder="Your Message *"
                value={form.message}
                onChange={handleChange}
                className={`bg-transparent border-b-2 p-3 w-full text-white placeholder-slate-400/70 focus:outline-none transition-colors duration-200 ${
                  errors.message
                    ? "border-red-500/70 focus:border-red-400/80"
                    : "border-slate-600/70 focus:border-cyan-400"
                }`}
                required
              />
              {errors.message && (
                <p className="text-red-400 text-xs mt-1.5">{errors.message}</p>
              )}

              <button
                type="submit"
                className={`w-full font-bold py-3.5 px-4 rounded-lg transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-950 focus:ring-cyan-400/60 ${
                  isSubmitting
                    ? "bg-slate-600/60 text-slate-400 cursor-wait"
                    : "bg-gradient-to-br from-cyan-300/70 via-cyan-400/60 to-cyan-500/70 text-slate-900 hover:scale-[1.02] shadow-lg hover:shadow-cyan-500/30"
                }`}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
      <style>{`
        .glow-text-cyan {
          text-shadow: 0 0 8px rgba(34, 211, 238, 0.5), 0 0 12px rgba(34, 211, 238, 0.3);
        }
      `}</style>
    </section>
  );
};
